import { useEffect, useRef, useState } from "react";
import { useAuth, formatApiError } from "../lib/auth";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { Mail, Phone, User, Shield, KeyRound, Send, Lock } from "lucide-react";

export default function Profile() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  if (!user) return null;

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-12">
      <div className="text-xs font-semibold tracking-[0.2em] uppercase text-cyan-400">Account</div>
      <h1 className="mt-2 text-4xl font-bold tracking-tight">Profile</h1>
      <div className="mt-10 p-8 rounded-2xl border border-zinc-800 bg-zinc-950/60">
        <div className="flex items-center gap-5 pb-6 border-b border-zinc-800">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-400 to-violet-500 flex items-center justify-center text-zinc-950 text-2xl font-bold">
            {user.full_name?.[0]?.toUpperCase()}
          </div>
          <div>
            <div className="text-xl font-semibold">{user.full_name}</div>
            <div className="text-sm text-zinc-500 capitalize">{user.role} account</div>
          </div>
        </div>
        <div className="mt-6 grid sm:grid-cols-2 gap-4">
          <Item icon={User} k="Full Name" v={user.full_name} />
          <Item icon={Shield} k="Role" v={user.role} />
          <Item icon={Mail} k="Email" v={user.email} />
          <Item icon={Phone} k="Phone" v={user.phone} />
        </div>
      </div>

      <div className="mt-6 p-8 rounded-2xl border border-zinc-800 bg-zinc-950/60">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-zinc-900 border border-zinc-800 flex items-center justify-center text-cyan-400">
              <Lock className="w-5 h-5" />
            </div>
            <div>
              <div className="font-semibold">Password</div>
              <div className="text-sm text-zinc-500">We never show or send your existing password. Change it any time with an email code.</div>
            </div>
          </div>
          <Button
            onClick={() => setOpen(true)}
            data-testid="change-password-btn"
            className="rounded-full bg-gradient-to-r from-cyan-400 to-violet-500 text-zinc-950 font-semibold"
          >
            <KeyRound className="w-4 h-4 mr-2" /> Change Password
          </Button>
        </div>
      </div>

      <ChangePasswordDialog open={open} onClose={() => setOpen(false)} email={user.email} />
    </div>
  );
}

function Item({ icon: Icon, k, v }) {
  return (
    <div className="p-4 rounded-xl bg-zinc-900/60 border border-zinc-800">
      <div className="flex items-center gap-2 text-xs text-zinc-500 uppercase tracking-wider">
        <Icon className="w-3.5 h-3.5" /> {k}
      </div>
      <div className="mt-2 text-zinc-100">{v}</div>
    </div>
  );
}

function ChangePasswordDialog({ open, onClose, email }) {
  const [step, setStep] = useState(1);          // 1 = request/sent, 2 = verify
  const [otp, setOtp] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (open) {
      setStep(1); setOtp(""); setPw(""); setPw2(""); setCooldown(0);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [open]);

  const startCooldown = (s) => {
    setCooldown(s);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCooldown((c) => {
        if (c <= 1) { clearInterval(timerRef.current); return 0; }
        return c - 1;
      });
    }, 1000);
  };

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password/init");
      toast.success(data?.message || "Verification code sent");
      setStep(2);
      startCooldown(data?.cooldown_seconds || 60);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to send code");
    } finally { setBusy(false); }
  };

  const resend = async () => {
    if (cooldown > 0) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password/resend");
      toast.success("A fresh code has been emailed to you.");
      startCooldown(data?.cooldown_seconds || 60);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not resend");
    } finally { setBusy(false); }
  };

  const verify = async () => {
    if (!otp.trim()) return toast.error("Enter the code from your email");
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    if (pw !== pw2) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/change-password/verify", { otp: otp.trim(), new_password: pw });
      toast.success("Password changed. A confirmation email has been sent.");
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Verification failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle>Change your password</DialogTitle>
          <DialogDescription className="text-zinc-500">
            We'll email a single-use verification code to <span className="text-cyan-400 font-mono">{email}</span>. Enter the code and choose a new password.
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-zinc-900 border border-zinc-800 text-sm text-zinc-400">
              For your security we don't display or email your current password. Instead, we send a one-time 6-digit code that expires in 10 minutes.
            </div>
            <Button
              onClick={send}
              disabled={busy}
              data-testid="change-password-send-otp"
              className="w-full rounded-full bg-gradient-to-r from-cyan-400 to-violet-500 text-zinc-950 font-semibold"
            >
              <Send className="w-4 h-4 mr-2" /> {busy ? "Sending..." : "Send verification code"}
            </Button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">6-digit code</Label>
              <Input
                data-testid="change-password-otp"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 8))}
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="123456"
                className="mt-1.5 bg-zinc-900 border-zinc-800 tracking-[0.4em] font-mono text-lg"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">New password</Label>
              <Input
                data-testid="change-password-new"
                type="password"
                minLength={6}
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                className="mt-1.5 bg-zinc-900 border-zinc-800"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Confirm password</Label>
              <Input
                data-testid="change-password-confirm"
                type="password"
                minLength={6}
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                className="mt-1.5 bg-zinc-900 border-zinc-800"
              />
            </div>
            <div className="flex items-center justify-between text-xs">
              <button
                type="button"
                onClick={resend}
                disabled={cooldown > 0 || busy}
                data-testid="change-password-resend"
                className="text-cyan-400 hover:underline disabled:text-zinc-600 disabled:no-underline"
              >
                {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
              </button>
              <span className="text-zinc-500">Code expires in 10 minutes</span>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-zinc-700 hover:bg-zinc-900">Cancel</Button>
          {step === 2 && (
            <Button
              onClick={verify}
              disabled={busy}
              data-testid="change-password-verify"
              className="bg-gradient-to-r from-cyan-400 to-violet-500 text-zinc-950 font-semibold"
            >
              {busy ? "Updating..." : "Confirm & change password"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
